import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, State, StateContext, Store, ofActionSuccessful, InitState } from '@ngxs/store';
import { patch, removeItem } from '@ngxs/store/operators';
import { combineLatest, EMPTY } from 'rxjs';
import { catchError, map, tap, distinctUntilChanged, filter } from 'rxjs/operators';
import { LongRunningContextCategoryTyp } from 'src/app/samwebDescription/samweb/waitingContext/waitingContextCategory';
import { ChatsService } from 'src/generated/swagger';
import { LongRunningContextStarted, LongRunningContextStopped } from '../../server-communication/running-actions/running-actions.actions';
import { RestoreWindow, SetWindowIndex } from '../../utility/samweb-window/samweb-window.actions';
import { SamwebWindowState, WindowTypes } from '../../utility/samweb-window/samweb-window.state';
import {
   ChatDetailsLoaded,
   CloseChat,
   IncrementNumberOfUnreadMessages,
   OpenChat,
   RemoveChat,
   ResetNumberOfUnreadMessages,
   StartLoadingChatDetails,
   UpdateLastMessage,
   StartLoadingChatDetailsByUid,
   ChatDetailsByUidLoaded,
   StartReQueueChat,
   StartLoadingChatToken,
   ChatTokenLoaded,
   CheckStartLoadingChatToken,
   MarkChatAsClosedByPeer,
   GetChatDetailsByUid,
   GetChatDetailsById,
   NewChatGuestStatus,
   LoadChatGuestStatus,
} from './chats.actions';
import { ChatDetails } from './models/chat-details';
import { ChatId } from './models/chat-id';
import { ChatMessage } from './models/chat-message';
import _ from 'lodash';
import { StartQueueSelectionProcess } from 'src/app/store/samwin/queue-selection-dialog/queue-selection-dialog.actions';
import { QueueSelectionPurpose } from 'src/app/samwebDescription/samwin/QueueSelectionPurpose';
import { SamwinCallListState } from '../samwin-call-list/samwin-call-list.state';
import { SessionState } from '../../session/session.state';
import { EnabledFeaturesState } from '../../session/enabled-features/enabled-features.state';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { GuestStatus } from 'src/app/samwebDescription/samwin/GuestStatus';

export interface ChatsStateModel {
   chats: ChatStateModel[]; // maps are not supported by ngxs
   userToken: string;
}

export interface ChatStateModel {
   chatId: ChatId;
   chatDetails: ChatDetails;
   numberOfUnreadMessages: number | null;
   lastMessage: ChatMessage | null;
   areChatDetailsLoaded: boolean;
   isOpen: boolean;
   wasClosedByPeer: boolean;
   guestStatus: GuestStatus;
}

@State<ChatsStateModel>({
   name: 'chatsState',
   defaults: {
      chats: [],
      userToken: '',
   },
})
@Injectable()
export class ChatsState {
   private readonly rocketChatFeatures = [FeatureEnum.ALLOW_USE_OF_ROCKET_CHAT_COLLABORATION_FEATURES, FeatureEnum.ALLOW_HANDLING_OF_ROCKET_CHAT_LIVECHATS];

   public constructor(private readonly store: Store, private readonly chatService: ChatsService, private actions$: Actions) {
      actions$.pipe(ofActionSuccessful(InitState)).subscribe(() =>
         combineLatest([
            this.store.select(SessionState.getIsAuthenticated),
            this.store.select(EnabledFeaturesState.isAnyFeatureEnabled(this.rocketChatFeatures)),
         ])
            .pipe(
               map(([isAuthenticated, isAnyRocketChatFeatureEnabled]) => isAuthenticated && isAnyRocketChatFeatureEnabled),
               distinctUntilChanged(),
               filter((rocketChatAgentIsAuthenticated) => rocketChatAgentIsAuthenticated)
            )
            .subscribe(() => this.store.dispatch(new CheckStartLoadingChatToken()))
      );
   }

   private static getDefaultChatState(chatId: ChatId): ChatStateModel {
      return {
         chatId: chatId,
         chatDetails: null,
         numberOfUnreadMessages: null,
         lastMessage: null,
         areChatDetailsLoaded: false,
         isOpen: false,
         wasClosedByPeer: false,
         guestStatus: GuestStatus.UNKNOWN,
      };
   }

   public static getChatState(state: ChatsStateModel, chatId: ChatId): [number, ChatStateModel] {
      const idx = state.chats.findIndex((chat) => chat.chatId.equals(chatId));
      if (idx >= 0) {
         return [idx, state.chats[idx]];
      } else {
         return [idx, ChatsState.getDefaultChatState(chatId)];
      }
   }

   public static getOpenChats(state: ChatsStateModel): ChatId[] {
      return state.chats.filter((chatState) => chatState.isOpen).map((chatState) => chatState.chatId);
   }

   public static isChatOpen(openChats: ChatId[]): (chatId: ChatId) => boolean {
      return (chatId: ChatId) => openChats.findIndex((item) => item.equals(chatId)) >= 0;
   }

   //#region Action Handling
   @Action(OpenChat)
   @ImmutableContext()
   public openChat(ctx: StateContext<ChatsStateModel>, action: OpenChat) {
      const isAlreadyOpen = ChatsState.isChatOpen(ChatsState.getOpenChats(ctx.getState()))(action.id);

      if (!isAlreadyOpen) {
         this.chatService.openChat(action.id.callId, action.id.messageId).subscribe();
      } else {
         const maxWindowIdx = this.store.selectSnapshot(SamwebWindowState.getHighestIndex);
         const windowId = _.isNil(action.id.uid) ? action.id.messageId : action.id.uid;
         this.store.dispatch(new SetWindowIndex(WindowTypes.Chat, windowId, maxWindowIdx + 1));
         this.store.dispatch(new RestoreWindow(WindowTypes.Chat, windowId));
      }
   }

   @Action(CloseChat)
   @ImmutableContext()
   public closeChat(ctx: StateContext<ChatsStateModel>, action: CloseChat) {
      return ctx.setState((state: ChatsStateModel) => {
         if (_.isNil(action.id.uid)) {
            this.chatService.closeChat(action.id.callId, action.id.messageId).subscribe();
         }
         const [idx, chatState] = ChatsState.getChatState(state, action.id);
         const newChatState = { ...chatState, isOpen: false };
         return this.upsertChatState(state, newChatState, idx);
      });
   }

   @Action(RemoveChat)
   public removeChat(ctx: StateContext<ChatsStateModel>, action: RemoveChat) {
      return ctx.setState(
         patch({
            chats: removeItem<ChatStateModel>((chat) => chat.chatId.equals(action.id)),
         })
      );
   }

   @Action(StartReQueueChat)
   @ImmutableContext()
   public startRequeueChat(ctx: StateContext<ChatsStateModel>, action: StartReQueueChat) {
      this.store.dispatch(
         new StartQueueSelectionProcess(QueueSelectionPurpose.REQUEUE, this.store.selectSnapshot(SamwinCallListState.getCallByIdFn)(action.id.callId))
      );
   }

   @Action(StartLoadingChatDetails)
   @ImmutableContext()
   public startLoadingChatDetails(ctx: StateContext<ChatsStateModel>, action: StartLoadingChatDetails) {
      return ctx.setState((state: ChatsStateModel) => {
         ctx.dispatch(new LongRunningContextStarted(LongRunningContextCategoryTyp.CHAT_WINDOW, action.id.messageId));
         if (_.isNil(state.userToken)) ctx.dispatch(new CheckStartLoadingChatToken());

         const [idx, chatState] = ChatsState.getChatState(state, action.id);
         if (chatState.areChatDetailsLoaded) return state;

         ctx.dispatch(new GetChatDetailsById(action.id));

         const newChatState = { ...chatState, isOpen: action.openChat };
         return this.upsertChatState(state, newChatState, idx);
      });
   }

   @Action(GetChatDetailsById)
   @ImmutableContext()
   public getChatDetailsById(ctx: StateContext<ChatsStateModel>, action: GetChatDetailsById) {
      return this.chatService.getChatDetails(action.id.callId, action.id.messageId).pipe(
         map((response) => {
            if (response.chatDetails === null) return null;
            const chatId = new ChatId(response.chatDetails.chatId.callId, response.chatDetails.chatId.messageId, response.chatDetails.chatId.uid);
            return new ChatDetails(chatId, response.chatDetails.chatConnector.rocketChatUrl, false, null);
         }),
         tap((chatDetails) => ctx.dispatch(new ChatDetailsLoaded(action.id, chatDetails))),
         catchError((error) => {
            console.error('Failed to get chat details.', error);
            ctx.dispatch(new ChatDetailsLoaded(action.id, null));
            return EMPTY;
         })
      );
   }

   @Action(ChatDetailsLoaded)
   @ImmutableContext()
   public chatDetailsLoaded(ctx: StateContext<ChatsStateModel>, action: ChatDetailsLoaded) {
      return ctx.setState((state: ChatsStateModel) => {
         const [idx, chatState] = ChatsState.getChatState(state, action.id);
         const newChatState = { ...chatState, chatDetails: action.chatDetails, areChatDetailsLoaded: true };
         ctx.dispatch(new LongRunningContextStopped(LongRunningContextCategoryTyp.CHAT_WINDOW, action.id.messageId));

         return this.upsertChatState(state, newChatState, idx);
      });
   }

   @Action(StartLoadingChatDetailsByUid)
   @ImmutableContext()
   public startLoadingChatDetailsByUid(ctx: StateContext<ChatsStateModel>, action: StartLoadingChatDetailsByUid) {
      return ctx.setState((state: ChatsStateModel) => {
         if (_.isNil(state.userToken)) ctx.dispatch(new CheckStartLoadingChatToken());

         const [idx, chatState] = ChatsState.getChatState(state, action.id);
         if (!chatState.areChatDetailsLoaded) {
            ctx.dispatch(new LongRunningContextStarted(LongRunningContextCategoryTyp.CHAT_WINDOW, action.id.uid));
            ctx.dispatch(new GetChatDetailsByUid(action.id, action.openChat));
         }
         const newChatState = { ...chatState, isOpen: action.openChat };
         return this.upsertChatState(state, newChatState, idx);
      });
   }

   @Action(GetChatDetailsByUid)
   @ImmutableContext()
   public getChatDetailsByUid(ctx: StateContext<ChatsStateModel>, action: GetChatDetailsByUid) {
      return this.chatService.getChatDetailsByUid(action.id.uid).pipe(
         map((response) => {
            if (response.chatDetails === null) return null;
            const chatId = new ChatId(response.chatDetails.chatId.callId, response.chatDetails.chatId.messageId, response.chatDetails.chatId.uid);
            return new ChatDetails(chatId, response.chatDetails.chatConnector.rocketChatUrl, true, null);
         }),
         tap((chatDetails) => ctx.dispatch(new ChatDetailsByUidLoaded(chatDetails.id, chatDetails, action.openChat))),
         catchError((error) => {
            console.error('Failed to get chat details.', error);
            ctx.dispatch(new ChatDetailsByUidLoaded(action.id, null, action.openChat));
            return EMPTY;
         })
      );
   }

   @Action(ChatDetailsByUidLoaded)
   @ImmutableContext()
   public chatDetailsByUidLoaded(ctx: StateContext<ChatsStateModel>, action: ChatDetailsByUidLoaded) {
      return ctx.setState((state: ChatsStateModel) => {
         const [idx, chatState] = ChatsState.getChatState(state, action.id);
         const newChatState = { ...chatState, chatDetails: action.chatDetails, areChatDetailsLoaded: true, isOpen: action.openChat };
         ctx.dispatch(new LongRunningContextStopped(LongRunningContextCategoryTyp.CHAT_WINDOW, action.id.uid));
         return this.upsertChatState(state, newChatState, idx);
      });
   }

   @Action(UpdateLastMessage)
   @ImmutableContext()
   public updateLastMessage(ctx: StateContext<ChatsStateModel>, action: UpdateLastMessage) {
      return ctx.setState((state: ChatsStateModel) => {
         const [idx, chatState] = ChatsState.getChatState(state, action.id);
         const newChatState = { ...chatState, lastMessage: action.lastMessage };

         return this.upsertChatState(state, newChatState, idx);
      });
   }

   @Action(IncrementNumberOfUnreadMessages)
   @ImmutableContext()
   public incrementNumberOfUnreadMessages(ctx: StateContext<ChatsStateModel>, action: IncrementNumberOfUnreadMessages) {
      return ctx.setState((state: ChatsStateModel) => {
         const [idx, chatState] = ChatsState.getChatState(state, action.id);
         const newChatState = { ...chatState, numberOfUnreadMessages: chatState.numberOfUnreadMessages + 1 };

         return this.upsertChatState(state, newChatState, idx);
      });
   }

   @Action(ResetNumberOfUnreadMessages)
   @ImmutableContext()
   public resetNumberOfUnreadMessages(ctx: StateContext<ChatsStateModel>, action: ResetNumberOfUnreadMessages) {
      return ctx.setState((state: ChatsStateModel) => {
         const [idx, chatState] = ChatsState.getChatState(state, action.id);
         const newChatState = { ...chatState, numberOfUnreadMessages: 0 };

         return this.upsertChatState(state, newChatState, idx);
      });
   }

   @Action(CheckStartLoadingChatToken)
   @ImmutableContext()
   public checkStartLoadingChatToken(ctx: StateContext<ChatsStateModel>, action: CheckStartLoadingChatToken) {
      if (this.isAuthenticatedUserWithRocketChatFeatures()) {
         this.store.dispatch(new StartLoadingChatToken());
      }
   }

   @Action(StartLoadingChatToken)
   @ImmutableContext()
   public startLoadingUserToken(ctx: StateContext<ChatsStateModel>, action: StartLoadingChatToken) {
      return this.chatService.getChatToken().pipe(
         map((result) => {
            ctx.dispatch(new ChatTokenLoaded(result.authToken.token));
         }),
         catchError((error) => {
            console.error('Failed to get user token', error);
            return EMPTY;
         })
      );
   }

   @Action(ChatTokenLoaded)
   public chatTokenLoaded(ctx: StateContext<ChatsStateModel>, action: ChatTokenLoaded) {
      return ctx.patchState({
         userToken: action.token,
      });
   }

   @Action(MarkChatAsClosedByPeer)
   @ImmutableContext()
   public markChatAsClosedByPeer(ctx: StateContext<ChatsStateModel>, action: MarkChatAsClosedByPeer) {
      return ctx.setState((state: ChatsStateModel) => {
         const [idx, chatState] = ChatsState.getChatState(state, action.id);
         const newChatState = { ...chatState, wasClosedByPeer: true, guestStatus: GuestStatus.UNKNOWN };

         return this.upsertChatState(state, newChatState, idx);
      });
   }

   @Action(LoadChatGuestStatus)
   @ImmutableContext()
   public startLoadingGuestStatus(ctx: StateContext<ChatsStateModel>, action: LoadChatGuestStatus) {
      const [idx, chatState] = ChatsState.getChatState(ctx.getState(), action.id);
      if (chatState.wasClosedByPeer || !chatState.isOpen) {
         return;
      }
      return this.chatService.getChatGuestInfo(action.id.callId, action.id.messageId).pipe(
         map((result) => {
            ctx.dispatch(new NewChatGuestStatus(action.id, result.guestInfo.status));
         }),
         catchError((error) => {
            console.error('Failed get chat guest info', error);
            return EMPTY;
         })
      );
   }

   @Action(NewChatGuestStatus)
   @ImmutableContext()
   public updateGuestStatus(ctx: StateContext<ChatsStateModel>, action: NewChatGuestStatus) {
      return ctx.setState((state: ChatsStateModel) => {
         const [idx, chatState] = ChatsState.getChatState(state, action.id);
         const newChatState = { ...chatState, guestStatus: GuestStatus.valueByString(action.status) };
         return this.upsertChatState(state, newChatState, idx);
      });
   }

   //#endregion

   private upsertChatState(state: ChatsStateModel, chatState: ChatStateModel, idx: number) {
      if (idx >= 0) {
         state.chats[idx] = chatState;
      } else {
         state.chats.push(chatState);
      }
      return state;
   }

   private isAuthenticatedUserWithRocketChatFeatures(): boolean {
      const isAuthenticated = this.store.selectSnapshot(SessionState.getIsAuthenticated);
      const hasRocketChatFeatures = this.store.selectSnapshot(EnabledFeaturesState.isAnyFeatureEnabled(this.rocketChatFeatures));

      return isAuthenticated && hasRocketChatFeatures;
   }
}
